import TimeLayer from "./TimeLayer";

import moment from "moment-timezone";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import CircleStyle from "ol/style/Circle";
import Stroke from "ol/style/Stroke";
import GeoJSON from "ol/format/GeoJSON";

const latlon = "EPSG:4326";

class NysmLightningLayer extends TimeLayer {
  constructor(opt_options) {
    const options = opt_options || {};
    const baseOptions = Object.assign({}, options);
    super(baseOptions);

    this._mapProjection = options.mapProjection;

    this._last_check = null;
    this._poll_interval = moment.duration(30, "seconds");
    this._ongoing_poll = null;

    this._is_polling = false;
    this._polling_timerId = null;
  
    this.load = this.load.bind(this);
    this._realtime_data_received = this._realtime_data_received.bind(this);
    this._make_layer = this._make_layer.bind(this);

    this._subscribedToTracking = false;

    this._max_lag = moment.duration(10, "minutes");
    this._interval = moment.duration(5, "minutes");

  }

  load(timeManager) {
    const now = moment.utc();
    let endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._max_lag);
    // detect realtime request
    if (
      timeManager.isTracking ||
      now.clone().subtract(this._max_lag) < endTime
    ) {
      endTime = null;
    }
    // if realtime request and we are tracking realtime, see if we can
    // skip this one
    if (
      endTime === null &&
      this._subscribedToTracking &&
      (timeManager.start_date.isSame(timeManager.end_date) ||
        [...this._loaded_times].sort()[0] <=
          timeManager.start_date
            .clone()
            .subtract(this._interval)
            .valueOf())
    ) {
      return;
    }
    const endTimeFormat = endTime === null ? "latest" : endTime.toISOString();
    fetch(`https://api.nysmesonet.org/data/index.php/lightning/list/${startTimeDelta.toISOString()}/${endTimeFormat}`)
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        throw Error();
      })
      .then(res => {
        const new_times = res.times.map(time => moment.tz(time * 1000, "UTC"));
        this._layer_times_received(new_times, timeManager);
      })
      .catch(() => console.warn("Failed fetching list of lightning layer times"));
  }

  _layer_times_received(new_times, timeManager) {
    const startTime = timeManager.start_date;
    const endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._max_lag);
    // Filter to include only the range we care about.
    let addedOneEarly = false;
    let times = [];
    try {
      times = new_times.reverse().filter((t) => {
        if (t.isSameOrAfter(startTime) && t.isSameOrBefore(endTime)) {
          if (t.isSame(startTime)) {
            // Don't need to add one early, flag it as done
            addedOneEarly = true;
          }
          return true;
        }
        if (
          t.isBefore(startTime) &&
          t.isSameOrAfter(startTimeDelta) &&
          !addedOneEarly
        ) {
          addedOneEarly = true;
          return true;
        }
        return false;
      });
    } catch (err) {
      console.error(err);
      times = [];
    }
    // Filter times down to those that don't already have layers (or have layers pending), then
    // create layers for any new times.
    const times_loaded = this.getLayers()
      .getArray()
      .map((l) => l.get("time").valueOf());
    times
      .filter((t) => times_loaded.indexOf(t.valueOf()) === -1)
      .forEach((t) => {
        let wait = this._make_layer(t, this._mapProjection);
        wait.then((new_layer) => {
          if (new_layer === null) {
            console.warn("No layer to add");
            return;
          }
          this.getLayers().push(new_layer);
        });
      });
  }



  _make_layer(time, projection) {
    const style = [
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: null,
          stroke: new Stroke({color: 'red', width: 3})
        })
      }),
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: null,
          stroke: new Stroke({color: 'black', width: 1})
        })
      })
    ];
    const wait = fetch(`https://api.nysmesonet.org/data/index.php/lightning/get/${time.toISOString()}`)
      .then(res => res.json())
      .then(res => {
        let geojson = new GeoJSON({
          dataProjection: latlon,
          featureProjection: projection,
        });
        const layer = new VectorLayer({
          title: "Lightning",
          source: new VectorSource({
            features: geojson.readFeatures(res)
          }),
          visible: true,
          time: time,
          style: style
        })
        return layer;
      })
    return wait;
  }

  async _poll() {
    const now = moment();
    if (
      this._last_check === null ||
      now > this._last_check.clone().add(this._poll_interval)
    ) {
      if (!this._ongoing_poll) {
        this._ongoing_poll = fetch(
          "https://api.nysmesonet.org/data/index.php/lightning/list/latest"
        )
          .then(response => response.json());
      }
      try {
        const response = await this._ongoing_poll;
        const time = moment.tz(response.times[0], "UTC");
        this._last_check = moment();
      } catch (err) {
        console.warn("Error retrieving times for lightning layer");
      }
    }
  }

  _schedule_poll = () => {
    this._poll().then(() => {
      if (this._is_polling) {
        this._polling_timerId = setTimeout(
          this._schedule_poll,
          this._poll_interval
        );
      }
    });
  };

  track_realtime(isTracking, timeManager) {
    if (!isTracking && this._is_polling) {
      if (this._polling_timerId !== null) {
        clearTimeout(this._polling_timerId);
        this._is_polling = false;
      }
    } else if (isTracking && !this._is_polling) {
      this._is_polling = true;
      this._schedule_poll();
    }
  }

  _realtime_data_received(newTime, result) {
    this._layer_times_received([newTime], this._rtTimeManager);
  }
}

export default NysmLightningLayer;
